'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Globe, ArrowRight, Copy, Check } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

import { toast } from 'sonner';

type LoginForm = { email: string; password: string; remember: boolean };
type SignupForm = { name: string; email: string; password: string; confirm: string; agree: boolean };

const DEMO_ACCOUNTS = [
  { role: 'Business Owner', email: 'sarah.chen@bloomflorist.co', password: 'Bloom@2026' },
  { role: 'Marketing Manager', email: 'dev.kapoor@pixelshift.agency', password: 'Pixel@2026' },
  { role: 'Agency Admin', email: 'marco.ferretti@nexadigital.io', password: 'Nexa@2026' },
];

const CHANNEL_ICONS = [
  { icon: Globe, color: '#7C3AED', label: 'Website' },
  { icon: () => <svg viewBox="0 0 24 24" width="16" height="16" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>, color: '#1877F2', label: 'Facebook' },
  { icon: () => <svg viewBox="0 0 24 24" width="16" height="16" fill="#E1306C"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>, color: '#E1306C', label: 'Instagram' },
  { icon: () => <svg viewBox="0 0 24 24" width="16" height="16" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>, color: '#0A66C2', label: 'LinkedIn' },
  { icon: () => <svg viewBox="0 0 24 24" width="16" height="16" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>, color: '#25D366', label: 'WhatsApp' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
      title="Copy"
    >
      {copied ? <Check size={13} className="text-[var(--positive)]" /> : <Copy size={13} />}
    </button>
  );
}

export default function LoginPageClient() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { name: '', email: '', password: '', confirm: '', agree: false } });

  const fillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setTab('login');
    toast.success('Credentials filled — click Sign In to continue');
  };

  const onLogin = async (data: LoginForm) => {
    const valid = DEMO_ACCOUNTS.find(a => a.email === data.email && a.password === data.password);
    if (!valid) {
      loginForm.setError('email', { message: 'Invalid credentials — use the demo accounts below to sign in' });
      return;
    }
    setLoginLoading(true);
    await new Promise(r => setTimeout(r, 1200));
    setLoginLoading(false);
    toast.success(`Welcome back, ${valid.role}!`);
    window.location.href = '/analytics-dashboard';
  };

  const onSignup = async (data: SignupForm) => {
    if (data.password !== data.confirm) {
      signupForm.setError('confirm', { message: 'Passwords do not match' });
      return;
    }
    setSignupLoading(true);
    await new Promise(r => setTimeout(r, 1400));
    setSignupLoading(false);
    toast.success('Account created! Redirecting to your dashboard...');
    setTimeout(() => { window.location.href = '/analytics-dashboard'; }, 1000);
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[540px] bg-gradient-to-br from-[#4C1D95] via-[#7C3AED] to-[#06B6D4] p-10 relative overflow-hidden shrink-0">
        {/* Decorative QR pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-8 right-8 grid grid-cols-8 gap-1">
            {Array.from({ length: 64 }).map((_, i) => (
              <div
                key={`qr-dot-${i}`}
                className="h-3 w-3 rounded-sm bg-white"
                style={{ opacity: [0,1,0,1,1,0,1,0,1,0,1,1,0,1,0,1,1,1,0,0,1,1,0,1,0,1,1,0,1,0,1,1,0,0,1,0,1,1,1,0,1,0,0,1,1,0,1,0,1,1,0,1,0,0,1,1,0,1,1,0,1,0,1,1][i] }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <span className="text-white font-bold text-2xl tracking-tight">QRTrack</span>
          </div>
          <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
            Every scan tells<br />a story.
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-8">
            Generate QR codes for every channel, then watch real-time analytics reveal where your audience lives, what device they use, and when they engage.
          </p>

          {/* Channel icons */}
          <div className="flex flex-wrap gap-3 mb-10">
            {CHANNEL_ICONS.map((ch) => (
              <div
                key={`brand-channel-${ch.label}`}
                className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2 text-white text-sm font-medium"
              >
                <ch.icon size={16} style={{ color: ch.color === '#7C3AED' ? 'white' : ch.color }} />
                {ch.label}
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: '2.4M', label: 'Scans tracked' },
              { value: '18K', label: 'QR codes active' },
              { value: '94%', label: 'Mobile scans' },
            ].map((stat) => (
              <div key={`brand-stat-${stat.label}`} className="rounded-2xl bg-white/10 backdrop-blur-sm p-4">
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-white/65 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-white/50 text-xs">
          © 2026 QRTrack · Privacy Policy · Terms of Service
        </p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-xl text-foreground">QRTrack</span>
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-xl bg-muted p-1 mb-8">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`auth-tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200 ${
                  tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Login form */}
          {tab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5 fade-in">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your QRTrack account</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="login-email">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  className={`w-full rounded-xl border px-4 py-3 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    loginForm.formState.errors.email ? 'border-[var(--negative)]' : 'border-border focus:border-primary'
                  }`}
                  {...loginForm.register('email', { required: 'Email is required' })}
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="login-password">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={`w-full rounded-xl border px-4 py-3 pr-12 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                      loginForm.formState.errors.password ? 'border-[var(--negative)]' : 'border-border focus:border-primary'
                    }`}
                    {...loginForm.register('password', { required: 'Password is required' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border accent-primary"
                    {...loginForm.register('remember')}
                  />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <button type="button" className="text-sm font-semibold text-primary hover:underline">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-primary text-white py-3 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-70"
              >
                {loginLoading ? (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <>Sign In <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          {/* Signup form */}
          {tab === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4 fade-in">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
                <p className="text-sm text-muted-foreground mt-1">Start tracking QR scans in minutes</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-name">
                  Full name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Sarah Chen"
                  className={`w-full rounded-xl border px-4 py-3 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    signupForm.formState.errors.name ? 'border-[var(--negative)]' : 'border-border focus:border-primary'
                  }`}
                  {...signupForm.register('name', { required: 'Full name is required' })}
                />
                {signupForm.formState.errors.name && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{signupForm.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-email">
                  Work email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  className={`w-full rounded-xl border px-4 py-3 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    signupForm.formState.errors.email ? 'border-[var(--negative)]' : 'border-border focus:border-primary'
                  }`}
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                  })}
                />
                {signupForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-password">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    className={`w-full rounded-xl border px-4 py-3 pr-12 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                      signupForm.formState.errors.password ? 'border-[var(--negative)]' : 'border-border focus:border-primary'
                    }`}
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-confirm">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Repeat your password"
                    className={`w-full rounded-xl border px-4 py-3 pr-12 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                      signupForm.formState.errors.confirm ? 'border-[var(--negative)]' : 'border-border focus:border-primary'
                    }`}
                    {...signupForm.register('confirm', { required: 'Please confirm your password' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirm && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{signupForm.formState.errors.confirm.message}</p>
                )}
              </div>

              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mt-0.5 rounded border-border accent-primary"
                    {...signupForm.register('agree', { required: 'You must accept the terms to continue' })}
                  />
                  <span className="text-sm text-muted-foreground">
                    I agree to the{' '}
                    <span className="text-primary font-semibold cursor-pointer hover:underline">Terms of Service</span>
                    {' '}and{' '}
                    <span className="text-primary font-semibold cursor-pointer hover:underline">Privacy Policy</span>
                  </span>
                </label>
                {signupForm.formState.errors.agree && (
                  <p className="mt-1.5 text-xs text-[var(--negative)]">{signupForm.formState.errors.agree.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={signupLoading}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-primary text-white py-3 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-70"
              >
                {signupLoading ? (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <>Create Account <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          {/* Demo credentials */}
          <div className="mt-8 rounded-2xl border border-border bg-muted/50 p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Demo Accounts</p>
            <div className="space-y-2">
              {DEMO_ACCOUNTS.map((acc) => (
                <div key={`demo-${acc.role}`} className="flex items-center justify-between gap-2 rounded-xl bg-card border border-border px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-foreground">{acc.role}</p>
                    <p className="font-mono text-[11px] text-muted-foreground truncate">{acc.email}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <CopyButton text={acc.email} />
                    <CopyButton text={acc.password} />
                    <button
                      type="button"
                      onClick={() => fillCredentials(acc.email, acc.password)}
                      className="rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-[11px] font-bold px-2.5 py-1 transition-colors"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}